import { RestHandler, MockedRequest, DefaultRequestBody, rest } from 'msw';
import { CreateProcedureRequest } from '../../queries/use-create-procedure';
import { DeleteWorkpaperProcedureRequest } from '../../queries/use-delete-workpaper-procedure';
import { UpdateWorkpaperProceduresRequest } from '../../queries/use-update-workpaper-procedures';
import { PostResponseBody } from '../../types/api-response';
import { WorkpaperProcedure } from '../../types/workpaper-procedure';
import { parseDate } from '../../utilities/formatting';
import { DB } from '../db';

const routes: RestHandler<MockedRequest<DefaultRequestBody>>[] = [];

routes.push(
    /**
     * GET /workpapers/:id/procedures
     * Gets all workpaper procedures
     */
    rest.get('/workpapers/:id/procedures', (req, res, ctx) => {
        const workpaperId = req.params.id as string;
        const search = req.url.searchParams.get('search');
        const sort = req.url.searchParams.get('sort');
        const filter = req.url.searchParams.get('filter');

        let workpaperProcedures = DB.GetWorkpaperProcedures(workpaperId);

        if (search) {
            workpaperProcedures = workpaperProcedures.filter(
                (jp) => jp.name.toLowerCase().includes(search.toLowerCase()) || jp.description?.toLowerCase().includes(search.toLowerCase())
            );
        } else if (filter) {
            const filterObj = JSON.parse(decodeURIComponent(filter));

            if (filterObj.entityType) {
                const value = filterObj.entityType;
                if (value.includes('GENERAL')) {
                    workpaperProcedures = workpaperProcedures.filter((jp) => !jp.entityType);
                } else {
                    workpaperProcedures = workpaperProcedures.filter((jp) => value.some((v: string) => jp.entityType === v));
                }
            }

            if (filterObj.role) {
                const value = filterObj.role;
                workpaperProcedures = workpaperProcedures.filter((jp) => value.includes(jp.role));
            }

            if (filterObj.status) {
                const value = filterObj.status;
                workpaperProcedures = workpaperProcedures.filter((jp) => value.includes(jp.status));
            }

            if (filterObj.updatedAt) {
                const { base, condition, to } = filterObj.updatedAt;
                if (condition === '>') {
                    workpaperProcedures = workpaperProcedures.filter((jp) => new Date(parseDate(jp.updatedAt)) > new Date(base));
                } else if (condition === '=') {
                    workpaperProcedures = workpaperProcedures.filter((jp) => parseDate(jp.updatedAt) === base);
                } else if (condition === '<') {
                    workpaperProcedures = workpaperProcedures.filter((jp) => new Date(parseDate(jp.updatedAt)) < new Date(base));
                } else {
                    workpaperProcedures = workpaperProcedures.filter(
                        (jp) => new Date(parseDate(jp.updatedAt)) >= new Date(base) && new Date(parseDate(jp.updatedAt)) <= new Date(to)
                    );
                }
            }
        }

        if (sort) {
            const { order, option } = JSON.parse(decodeURIComponent(sort));

            if (option === 'Name') {
                workpaperProcedures = workpaperProcedures.sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1));
            } else if (option === 'Entity Type') {
                workpaperProcedures = workpaperProcedures.sort((a, b) =>
                    (a.entityType ? a.entityType.toLowerCase() : 'general') > (b.entityType ? b.entityType.toLowerCase() : 'general')
                        ? 1
                        : -1
                );
            } else if (option === 'Role') {
                workpaperProcedures = workpaperProcedures.sort((a, b) => (a.role.length > b.role.length ? 1 : -1));
            } else if (option === 'Last Updated') {
                workpaperProcedures = workpaperProcedures.sort((a, b) => (a.updatedAt > b.updatedAt ? 1 : -1));
            }
            if (order === 'desc') {
                workpaperProcedures = workpaperProcedures.reverse();
            }
        }

        return res(ctx.delay(), ctx.status(200), ctx.json({ workpaperProcedures }));
    }),

    /**
     * GET /workpapers/:workpaperId/procedures/:procedureId
     * Gets specific procedure
     */
    rest.get('/workpapers/:workpaperId/procedures/:procedureId', (req, res, ctx) => {
        const workpaperId = req.params.workpaperId as string;
        const procedureId = req.params.procedureId as string;
        const workpaperProcedure = DB.GetWorkpaperProcedure(workpaperId, procedureId);

        return res(ctx.delay(), ctx.status(200), ctx.json({ workpaperProcedure }));
    }),

    /**
     * POST /workpapers/:id/procedures
     * Creates a new procedure
     */
    rest.post<CreateProcedureRequest>('/workpapers/:id/procedures', (req, res, ctx) => {
        const workpaperId = req.params.id as string;
        const { workpaperProcedures: workpaperProceduresReq } = req.body;

        const response: PostResponseBody<WorkpaperProcedure> = {
            hasErrors: false,
            results: [],
        };

        workpaperProceduresReq.forEach(({ name, description, entityType, role }) => {
            try {
                const workpaperProcedure = DB.CreateWorkpaperProcedure(workpaperId, name, role, description, entityType);
                DB.CreateFolder(workpaperProcedure.id, workpaperProcedure.name);
                response.results.push({ statusCode: 201, data: workpaperProcedure });
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
            } catch (error: any) {
                const fields: { [key: string]: string } = {};
                if (error.message === 'A procedure must have a name.') {
                    fields.name = 'A procedure must have a name.';
                }
                if (error.message === 'A procedure with this name already exists.') {
                    fields.name = 'A procedure with this name already exists.';
                }
                if (error.message === 'A procedure must have a role.') {
                    fields.role = 'A procedure must have a role.';
                }
                response.hasErrors = true;
                response.results.push({
                    statusCode: 400,
                    error: { original: { name, description, entityType, role }, fields },
                });
            }
        });

        return res(ctx.delay(), ctx.status(response.hasErrors ? 400 : 200), ctx.json(response));
    }),

    /**
     * PATCH /workpapers/:id/procedures
     * Updates a procedure item status for the specified workpaper
     */
    rest.patch<UpdateWorkpaperProceduresRequest>('/workpapers/:id/procedures', (req, res, ctx) => {
        const id = req.params.id as string;
        const response: PostResponseBody<WorkpaperProcedure> = {
            hasErrors: false,
            results: [],
        };

        for (const proceduresReq of req.body) {
            const fields: { [key: string]: string } = {};
            if (proceduresReq.fields.status) {
                DB.UpdateWorkpaperProceduresStatus(proceduresReq.id, proceduresReq.fields.status, proceduresReq.fields.notApplicableReason);
            } else if (proceduresReq.fields.name && proceduresReq.fields.role) {
                // Check for duplicate name
                const existingProcedure = DB.GetWorkpaperProcedures(id).find(
                    (jp) => jp.name.toLowerCase() === proceduresReq.fields.name?.toLowerCase() && proceduresReq.id !== jp.id
                );

                if (existingProcedure) {
                    fields.name = 'A procedure with this name already exists.';
                    response.hasErrors = true;
                    response.results.push({
                        statusCode: 400,
                        error: {
                            original: { ...req },
                            fields,
                        },
                    });
                } else {
                    DB.UpdateWorkpaperProceduresDetails(
                        proceduresReq.id,
                        proceduresReq.fields.name,
                        proceduresReq.fields.role,
                        proceduresReq.fields.description,
                        proceduresReq.fields.entityType
                    );
                }
            } else {
                if (!proceduresReq.fields.name) {
                    fields.name = 'A procedure must have a name.';
                }
                if (!proceduresReq.fields.role) {
                    fields.role = 'A procedure must have a role.';
                }
                response.hasErrors = true;
                response.results.push({
                    statusCode: 400,
                    error: {
                        original: { ...req },
                        fields,
                    },
                });
            }
        }
        return res(ctx.delay(), ctx.status(response.hasErrors ? 400 : 200), ctx.json(response));
    }),

    /**
     * DELETE /workpapers/:workpaperId/procedures/:id
     * Delete a procedure item
     */
    rest.delete<DeleteWorkpaperProcedureRequest>('/workpapers/:workpaperId/procedures/:id', (req, res, ctx) => {
        const procedureId = req.params.id as string;
        DB.DeleteWorkpaperProcedure(procedureId);
        return res(ctx.delay(), ctx.status(204));
    })
);

export { routes };
